/**
 * @file ScreenUpgrader.ts
 * @description 底座屏幕固件升级器
 *
 * 基于 HID Output Report 协议向底座屏幕芯片写入固件。
 *
 * 协议格式 (参考 FirmwareUpgradeScreen.cpp):
 *   - HID Output Report ID: 0x00
 *   - 数据包大小: 512 bytes (DATA_MAX_LENGTH)
 *   - 有效数据块大小: 256 bytes (SEND_DATA_LENTH)
 *   - 帧头: 0x55, 0x10, 0x3A
 *   - 命令字: 0x12
 *   - 校验: 从长度字节开始的累加和 & 0xFF
 *   - 结尾: 0x0D, 0x0A
 */

import { BaseDevice } from '../../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { delay } from '../../../../utils/TimeUtils'

const DATA_MAX_LENGTH = 512
const SEND_DATA_LENTH = 256
const CMD_UPDATE_DATA = 0x12

const HEADER_1 = 0x55
const HEADER_2 = 0x10
const HEADER_3 = 0x3a // ASCII ':'

export class ScreenUpgrader {
  private device: BaseDevice
  private firmwareData: Uint8Array = new Uint8Array(0)

  private onProgressCallback: ((progress: number) => void) | null = null
  private onProgressFinishCallback: (() => void) | null = null

  constructor(device: BaseDevice) {
    this.device = device
  }

  // ==================== 公共方法 ====================

  setOnProgressCallback(callback: (progress: number) => void): void {
    this.onProgressCallback = callback
  }

  setOnProgressFinishCallback(callback: () => void): void {
    this.onProgressFinishCallback = callback
  }

  /**
   * 启动屏幕固件升级
   * @param firmwareData .bin 固件数据
   */
  async startUpdate(firmwareData: Uint8Array): Promise<void> {
    this.firmwareData = firmwareData

    try {
      console.log('[ScreenUpgrader] 开始写入屏幕固件...')
      await this.sendBinFileData()
      console.log('[ScreenUpgrader] 屏幕固件升级完成')
    } catch (error) {
      console.error('[ScreenUpgrader] 屏幕固件升级失败:', error)
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `Screen 升级失败: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    }
  }

  // ==================== 私有方法 ====================

  /**
   * 逐块发送固件数据
   *
   * 数据包格式 (512 bytes):
   *   [0]   保留 (0x00)
   *   [1]   0x55        — 帧头1
   *   [2]   0x10        — 帧头2
   *   [3]   0x3A        — 帧头3
   *   [4]   len & 0xFF  — 长度低字节 (dataLength + 2)
   *   [5]   len >> 8    — 长度高字节
   *   [6]   0x12        — CMD_UPDATE_DATA
   *   [7~n] 固件数据     — 最多 256 字节
   *   [n+1] checksum    — 字节[4]~[n] 累加和 & 0xFF
   *   [n+2] 0x0D        — CR
   *   [n+3] 0x0A        — LF
   */
  private async sendBinFileData(): Promise<void> {
    const totalLength = this.firmwareData.length
    let sendDataLength = 0

    while (sendDataLength < totalLength) {
      const dataLength = Math.min(SEND_DATA_LENTH, totalLength - sendDataLength)

      // 构建数据包: 512 字节缓冲区，默认全 0
      const buf = new Uint8Array(DATA_MAX_LENGTH).fill(0)

      // 帧头
      buf[1] = HEADER_1
      buf[2] = HEADER_2
      buf[3] = HEADER_3

      // 长度字段 = dataLength + 2 (命令字 + 数据)
      buf[4] = (dataLength + 2) & 0xff
      buf[5] = (dataLength + 2) >> 8

      // 命令字
      buf[6] = CMD_UPDATE_DATA

      // 固件数据块
      buf.set(this.firmwareData.subarray(sendDataLength, sendDataLength + dataLength), 7)

      // Checksum: 字节 [4] ~ [7 + dataLength - 1] 累加和 & 0xFF
      let checksum = 0
      for (let i = 4; i < 7 + dataLength; i++) {
        checksum += buf[i]
      }
      buf[7 + dataLength] = checksum & 0xff

      // 结尾 CRLF
      buf[7 + dataLength + 1] = 0x0d
      buf[7 + dataLength + 2] = 0x0a

      // 发送到设备
      await this.sendDeviceData(buf)

      sendDataLength += dataLength

      // 计算并上报进度
      const progress = Math.floor((sendDataLength * 100) / totalLength)
      this.emitSendUpdateProgress(progress)
    }

    // 完成
    this.emitSendUpdateProgress(100)
    this.emitUpdateFinished()
  }

  /**
   * 发送 Output Report 到设备并等待处理完毕。
   *
   * 与 WCHUpgrader 的 sendDeviceData 保持一致: 仅写 Output Report + delay,
   * 不做 Feature Report 回读。C++ 参考实现中 readDeviceData 的回读结果
   * (readFileDataBuf) 全程未被使用, 仅作为写后时序同步。
   */
  private async sendDeviceData(data: Uint8Array): Promise<void> {
    const reportId = 0x00
    await this.device.channel.sendOutputReport(reportId, data)
    await delay(5)
  }

  private emitSendUpdateProgress(percent: number): void {
    const p = Math.min(100, Math.max(0, Math.floor(percent * 100) / 100))
    if (this.onProgressCallback) {
      this.onProgressCallback(p)
      if (p === 100) {
        this.emitUpdateFinished()
      }
    }
  }

  private emitUpdateFinished(): void {
    if (this.onProgressFinishCallback) {
      this.onProgressFinishCallback()
    }
  }
}
